/**
 * ShelfRow: the one horizontal shelf used by every sideways row in the app.
 *
 * It shows a child that more is waiting to the right:
 * - card sizing so the last visible card peeks ~40% at the edge whenever the row overflows ("fit");
 * - soft edge fades only on the side that has more;
 * - big round Storybook arrows that page by the visible width (hidden when there is nothing that way);
 * - a one-time gentle nudge (row + arrow) the first time a shelf kind overflows (localStorage, reduced motion = none);
 * - scroll-snap per card, vertical mouse wheel → sideways, arrow keys / Home / End;
 * - a pencil-stroke progress bar with a "6/18" count;
 * - a final "See all (18)" card that opens a full-screen grid with the same items;
 * - Scribble waving "That's all!" at the very end.
 *
 * Variants: "cards" (default, all of the above) and "pan" (big painted walls: no resizing, no snap, no see-all, no cameo).
 */
import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../state/store';
import { useT } from '../lib/i18n';
import { play } from '../lib/sfx';
import { Icon } from './icons';
import { Scribble } from './characters';
import './ShelfRow.css';

export interface ShelfRowProps {
  /** Shelf type; the first-view nudge is remembered once per kind. */
  kind: string;
  children: ReactNode;
  variant?: 'cards' | 'pan';
  /** Classes on the scroller itself (keeps the screen's own track styles). */
  trackClassName?: string;
  /** Classes on the outer wrapper (layout in the parent). */
  className?: string;
  /** Accessible name of the scroller; or `labelledBy` (id of a heading). */
  label?: string;
  labelledBy?: string;
  /** Title of the "See all" sheet (falls back to `label`). */
  sheetTitle?: ReactNode;
  /** Items counted for the progress / see-all (default: direct children of the track). */
  itemSelector?: string;
  seeAll?: boolean;
  cameo?: boolean;
  /** Resize cards a little so a partial card always peeks (cards variant only). */
  fit?: boolean;
  testId?: string;
  trackProps?: Record<string, unknown>;
  /**
   * Capped shelf: the row shows only some of a longer list; a final "See more (N)" card (always shown) opens the full
   * list in the same grid sheet. No "That's all!" cameo (it is not the end).
   */
  more?: { total: number; items: ReactNode; onOpen?: () => void };
}

interface ShelfState {
  over: boolean;
  atStart: boolean;
  atEnd: boolean;
  shown: number;
  total: number;
  frac: number;
  thumb: number;
}

const START: ShelfState = { over: false, atStart: true, atEnd: true, shown: 0, total: 0, frac: 0, thumb: 1 };
const NUDGE_KEY = 'sketchling-shelf-nudge:';
const PEEK = 0.4;
const claimed = new Set<string>();

function nudgeSeen(kind: string): boolean {
  if (claimed.has(kind)) return true;
  try {
    return localStorage.getItem(NUDGE_KEY + kind) === '1';
  } catch {
    return false;
  }
}

function markNudge(kind: string) {
  claimed.add(kind);
  try {
    localStorage.setItem(NUDGE_KEY + kind, '1');
  } catch {
    /* private mode: once per session is fine */
  }
}

export function useReducedMotion(): boolean {
  const setting = useApp((s) => s.settings.reducedMotion);
  const [media, setMedia] = useState(() => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setMedia(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return setting || media;
}

export function ShelfRow({
  kind,
  children,
  variant = 'cards',
  trackClassName = '',
  className = '',
  label,
  labelledBy,
  sheetTitle,
  itemSelector,
  seeAll = true,
  cameo = true,
  fit = true,
  testId,
  trackProps,
  more,
}: ShelfRowProps) {
  const t = useT();
  const reduced = useReducedMotion();
  const track = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const natural = useRef<{ el: Element; w: number; lo: number; n: number; uniform: boolean } | null>(null);
  const page = useRef(0);
  const step = useRef(0);
  const [st, setSt] = useState<ShelfState>(START);
  const [nudge, setNudge] = useState(false);
  const [sheet, setSheet] = useState(false);
  const pan = variant === 'pan';
  const doFit = !pan && fit;
  const withExtras = !pan;
  const titleId = useId();

  const items = useCallback((): HTMLElement[] => {
    const el = track.current;
    if (!el) return [];
    if (itemSelector) return Array.from(el.querySelectorAll<HTMLElement>(itemSelector));
    return Array.from(el.children).filter((c): c is HTMLElement => c instanceof HTMLElement && !c.classList.contains('shelf-x'));
  }, [itemSelector]);

  /** Card sizing: n full cards + PEEK of the next one fill the visible width. */
  const size = useCallback(() => {
    const el = track.current;
    if (!el) return;
    const list = items();
    const cs = getComputedStyle(el);
    const gap = parseFloat(cs.columnGap) || 0;
    if (!doFit || !list.length) {
      const w = list[0]?.getBoundingClientRect().width ?? 0;
      step.current = w + gap;
      page.current = el.clientWidth * 0.85;
      return;
    }
    const first = list[0]!;
    if (!natural.current || natural.current.el !== first || natural.current.n !== list.length) {
      el.classList.remove('shelf-sized');
      const ws = list.map((it) => it.getBoundingClientRect().width).filter((x) => x > 0);
      const lo = Math.min(...ws);
      const hi = Math.max(...ws);
      natural.current = { el: first, w: ws[0] ?? 0, lo: ws.length ? lo : 0, n: list.length, uniform: ws.length > 0 && hi <= lo * 1.15 };
    }
    const w = natural.current.w;
    if (!natural.current.uniform) {
      // Mixed widths (e.g. remix tiles of three cards): keep their sizes; fades + arrows show the overflow.
      el.classList.remove('shelf-sized');
      step.current = w + gap;
      page.current = el.clientWidth * 0.85;
      return;
    }
    const view = el.clientWidth - (parseFloat(cs.paddingLeft) || 0);
    const content = list.length * (w + gap) - gap;
    if (w <= 0 || content <= view + 1) {
      el.classList.remove('shelf-sized');
      step.current = w + gap;
      page.current = view;
      return;
    }
    let best = { cw: w, n: 1, d: Infinity };
    for (let n = 1; n <= 12; n++) {
      const cw = (view - n * gap) / (n + PEEK);
      if (cw < w * 0.8 || cw > w * 1.2) continue;
      const d = Math.abs(cw - w);
      if (d < best.d) best = { cw, n, d };
    }
    if (!Number.isFinite(best.d)) {
      // Cards too big or too small to adjust within ±20 %: keep them and page by the visible width.
      el.classList.remove('shelf-sized');
      step.current = w + gap;
      page.current = Math.max(w + gap, Math.floor(view / (w + gap)) * (w + gap));
      return;
    }
    el.style.setProperty('--shelf-cw', `${best.cw.toFixed(1)}px`);
    el.classList.add('shelf-sized');
    step.current = best.cw + gap;
    page.current = best.n * (best.cw + gap);
  }, [doFit, items]);

  const measure = useCallback(() => {
    const el = track.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const over = max > 2;
    const list = items();
    const total = list.length;
    let shown = total;
    if (over && total) {
      const box = el.getBoundingClientRect();
      shown = 0;
      list.forEach((it, i) => {
        const r = it.getBoundingClientRect();
        if (r.right <= box.right + 2 && r.left < box.right) shown = i + 1;
      });
      if (el.scrollLeft >= max - 2) shown = total;
      shown = Math.max(1, shown);
    }
    // Snap may rest a few px in (card margins / scroll padding): the first card's snap point counts as the start.
    const firstAt = list[0] ? Math.max(0, list[0].getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft - 4) : 0;
    const next: ShelfState = {
      over,
      atStart: !over || el.scrollLeft <= Math.min(firstAt, 24) + 2,
      atEnd: !over || el.scrollLeft >= max - 2,
      shown,
      total,
      frac: over ? Math.min(1, Math.max(0, el.scrollLeft / max)) : 0,
      thumb: over ? Math.max(0.12, el.clientWidth / el.scrollWidth) : 1,
    };
    setSt((s) => (s.over === next.over && s.atStart === next.atStart && s.atEnd === next.atEnd && s.shown === next.shown && s.total === next.total && Math.abs(s.frac - next.frac) < 0.004 && Math.abs(s.thumb - next.thumb) < 0.004 ? s : next));
  }, [items]);

  useLayoutEffect(() => {
    size();
    measure();
  });

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    const onResize = () => {
      size();
      measure();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(onResize) : null;
    ro?.observe(el);
    // Vertical mouse wheel → sideways while there is room that way; at an edge the page scrolls on.
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 2) return;
      if ((e.deltaY < 0 && el.scrollLeft <= 0) || (e.deltaY > 0 && el.scrollLeft >= max - 1)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY * (e.deltaMode === 1 ? 32 : e.deltaMode === 2 ? el.clientWidth : 1);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    // Fonts / lazy images change card sizes after the first paint.
    const late = window.setTimeout(onResize, 400);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', onWheel);
      ro?.disconnect();
      window.clearTimeout(late);
    };
  }, [measure, size]);

  // One gentle nudge per shelf kind, the first time it overflows on screen.
  useEffect(() => {
    if (!st.over || reduced || nudgeSeen(kind)) return;
    const el = root.current;
    if (!el) return;
    let timer = 0;
    const go = () => {
      if (nudgeSeen(kind)) return;
      markNudge(kind);
      setNudge(true);
      timer = window.setTimeout(() => setNudge(false), 1900);
    };
    if (typeof IntersectionObserver !== 'function') {
      go();
      return () => window.clearTimeout(timer);
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((x) => x.isIntersecting && x.intersectionRatio >= 0.5)) {
          io.disconnect();
          timer = window.setTimeout(go, 350);
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      window.clearTimeout(timer);
    };
  }, [st.over, reduced, kind]);

  const scrollByPx = (dx: number) => track.current?.scrollBy({ left: dx, behavior: reduced ? 'auto' : 'smooth' });
  /** Page by the visible width, landing on a card start: next = the card cut at the right edge comes first. */
  const pageBy = (dir: 1 | -1) => {
    play('click');
    const el = track.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const starts = items()
      .map((it) => it.getBoundingClientRect())
      .map((r) => ({ x: r.left - box.left + el.scrollLeft - 4, right: r.right - box.left }));
    const behavior = reduced ? 'auto' : 'smooth';
    if (pan || !starts.length) return scrollByPx(dir * (page.current || el.clientWidth * 0.85));
    if (dir > 0) {
      const cut = starts.find((c) => c.right > el.clientWidth + 2);
      el.scrollTo({ left: cut ? Math.max(cut.x, el.scrollLeft + 40) : el.scrollWidth, behavior });
    } else {
      const want = el.scrollLeft - (page.current || el.clientWidth * 0.85);
      const back = starts.find((c) => c.x >= want - 2);
      el.scrollTo({ left: Math.max(0, back && back.x < el.scrollLeft - 2 ? back.x : want), behavior });
    }
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    const el = track.current;
    if (!el || !st.over) return;
    const one = step.current || 160;
    if (e.key === 'ArrowRight') scrollByPx(one);
    else if (e.key === 'ArrowLeft') scrollByPx(-one);
    else if (e.key === 'PageDown') scrollByPx(page.current);
    else if (e.key === 'PageUp') scrollByPx(-page.current);
    else if (e.key === 'Home') el.scrollTo({ left: 0, behavior: reduced ? 'auto' : 'smooth' });
    else if (e.key === 'End') el.scrollTo({ left: el.scrollWidth, behavior: reduced ? 'auto' : 'smooth' });
    else return;
    e.preventDefault();
  };

  const total = st.total;
  const showSeeAll = withExtras && (more ? more.total > total : seeAll && st.over && total > 2);
  const showCameo = withExtras && cameo && st.over && !more;
  // the sheet's column width: the narrowest card (a remix tile of three cards first in the row must not size every column)
  const nat = natural.current?.lo || natural.current?.w;
  const cls = [
    'shelf',
    `shelf-${variant}`,
    st.over ? 'is-over' : '',
    st.over && !st.atStart ? 'can-l' : '',
    st.over && !st.atEnd ? 'can-r' : '',
    nudge ? 'nudge' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const sheetItems = Children.toArray(children).filter(isValidElement);

  return (
    <div className={cls} ref={root} data-shelf={kind} data-testid={testId} data-over={st.over ? 'true' : 'false'} data-nudge={nudge ? 'true' : 'false'}>
      <div className="shelf-stage">
        <div
          role="region"
          aria-label={label}
          aria-labelledby={label ? undefined : labelledBy}
          data-testid="shelf-track"
          {...trackProps}
          ref={track}
          className={`shelf-track ${trackClassName}`}
          tabIndex={st.over ? 0 : -1}
          onKeyDown={onKey}
        >
          {children}
          {showSeeAll && (
            <button
              type="button"
              className={`shelf-x shelf-seeall${more ? ' shelf-more' : ''}`}
              onClick={() => {
                play('drawer');
                more?.onOpen?.();
                setSheet(true);
              }}
              data-testid="shelf-seeall"
              data-total={more ? more.total : total}
            >
              <span className="shelf-seeall-stack" aria-hidden>
                <i />
                <i />
                <i />
              </span>
              <b>{more ? t('shelf.seeMore') : t('shelf.seeAll')}</b>
              <small>({more ? more.total : total})</small>
            </button>
          )}
          {showCameo && (
            <div className="shelf-x shelf-end" data-testid="shelf-end" aria-hidden={!st.atEnd}>
              <Scribble mood="wave" size={64} className={st.atEnd && !reduced ? 'shelf-wave' : ''} />
              <span className="shelf-bubble">{t('shelf.thatsAll')}</span>
            </div>
          )}
        </div>
        {st.over && !st.atStart && (
          <button type="button" className="shelf-arrow prev" onClick={() => pageBy(-1)} aria-label={t('shelf.prev')} data-testid="shelf-prev">
            <Icon name="back" size={26} />
          </button>
        )}
        {st.over && !st.atEnd && (
          <button type="button" className="shelf-arrow next" onClick={() => pageBy(1)} aria-label={t('shelf.next')} data-testid="shelf-next">
            <Icon name="forward" size={26} />
          </button>
        )}
      </div>
      {st.over && (
        <div className="shelf-progress" aria-hidden data-testid="shelf-progress">
          <span className="shelf-rail">
            <span className="shelf-thumb" style={{ left: `${(st.frac * (1 - st.thumb) * 100).toFixed(2)}%`, width: `${(st.thumb * 100).toFixed(2)}%` } as CSSProperties} />
          </span>
          <span className="shelf-count" data-testid="shelf-count">
            {st.shown}/{total}
          </span>
        </div>
      )}
      {sheet &&
        createPortal(
          <ShelfSheet title={sheetTitle ?? label} titleId={titleId} onClose={() => setSheet(false)} count={more ? more.total : total} cardWidth={nat}>
            {more ? more.items : sheetItems}
          </ShelfSheet>,
          document.body,
        )}
    </div>
  );
}

/** Section order inside the sheet (items say which one they belong to with `data-sheet-group`). */
const SHEET_GROUPS = ['lessons', 'remix', 'crossovers', 'pictures', 'doodles'] as const;
const SEC_KEY = {
  lessons: 'shelf.sec.lessons',
  remix: 'shelf.sec.remix',
  crossovers: 'shelf.sec.crossovers',
  pictures: 'shelf.sec.pictures',
  doodles: 'shelf.sec.doodles',
} as const;
/** Lists longer than this skip rendering the rows far off screen (content-visibility; thumbnails mount in view). */
const LONG_SHEET = 24;

/**
 * "See all" sheet: a full-screen responsive grid of the shelf's items.
 * - columns: auto-fill, sized from the shelf's narrowest card (clamped by size class), never fewer than 2;
 * - an item can ask for several columns (`data-sheet-span`, e.g. a remix tile of three cards); it is capped at the
 *   number of columns and its cards share the width;
 * - when the items come from several groups (`data-sheet-group`: lessons / remix / pictures …) the grid shows a
 *   sticky header per group with its count;
 * - the header (title + count + close) never scrolls and respects the safe areas; open / close animate.
 */
function ShelfSheet({ title, titleId, onClose, count, cardWidth, children }: { title?: ReactNode; titleId: string; onClose: () => void; count: number; cardWidth?: number; children: ReactNode }) {
  const t = useT();
  const reduced = useReducedMotion();
  const grid = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const [groups, setGroups] = useState<{ id: string; n: number; order: number }[]>([]);
  const close = useCallback(() => {
    if (reduced) return onClose();
    setClosing(true);
    window.setTimeout(onClose, 170);
  }, [onClose, reduced]);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  // Groups + spans read from the rendered items (the shelf's children can be fragments of any component).
  useLayoutEffect(() => {
    const el = grid.current;
    if (!el) return;
    const items = Array.from(el.children).filter((c): c is HTMLElement => c instanceof HTMLElement && !c.classList.contains('shelf-sec'));
    const counts = new Map<string, number>();
    for (const it of items) {
      const g = it.dataset.sheetGroup;
      if (g) counts.set(g, (counts.get(g) ?? 0) + 1);
    }
    const sectioned = counts.size >= 2 && items.every((it) => it.dataset.sheetGroup);
    const ids = [...counts.keys()].sort((a, b) => rank(a) - rank(b));
    const next = sectioned ? ids.map((id, k) => ({ id, n: counts.get(id)!, order: 2 * k })) : [];
    for (const it of items) it.style.order = sectioned ? String(2 * ids.indexOf(it.dataset.sheetGroup!) + 1) : '';
    setGroups((g) => (g.length === next.length && g.every((x, k) => x.id === next[k]!.id && x.n === next[k]!.n) ? g : next));
    el.classList.toggle('is-long', items.length > LONG_SHEET);
    const span = () => {
      const cols = Math.max(1, getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length);
      for (const it of items) {
        const want = Number(it.dataset.sheetSpan) || 1;
        const n = Math.min(cols, want);
        it.style.gridColumn = n > 1 ? `span ${n}` : '';
      }
    };
    span();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(span) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [children]);

  return (
    <div className={`shelf-sheet${closing ? ' is-closing' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid="shelf-sheet">
      <header className="shelf-sheet-head" data-testid="shelf-sheet-head">
        <h2 id={titleId}>{title ?? t('shelf.seeAll')}</h2>
        <small className="shelf-sheet-count" data-testid="shelf-sheet-count">
          {t('shelf.items', { n: count })}
        </small>
        <button type="button" className="shelf-arrow shelf-close" onClick={() => (play('click'), close())} aria-label={t('close')} data-testid="shelf-sheet-close">
          <Icon name="close" size={24} />
        </button>
      </header>
      <div
        className="shelf-grid"
        ref={grid}
        style={{ ['--shelf-nat' as string]: `${Math.round(cardWidth || 150)}px` }}
        data-testid="shelf-grid"
        onClick={(e) => {
          // Opening an item (a lesson, a modal) closes the sheet; a heart / favourite tap keeps it open.
          const hit = (e.target as HTMLElement).closest('button, a');
          if (hit && !hit.closest('.heart, [data-shelf-keep]')) window.setTimeout(onClose, 0);
        }}
      >
        {groups.map((g) => (
          <h3 key={g.id} className="shelf-sec" style={{ order: g.order }} data-testid={`shelf-sec-${g.id}`}>
            {g.id in SEC_KEY ? t(SEC_KEY[g.id as keyof typeof SEC_KEY]) : g.id} <small>{g.n}</small>
          </h3>
        ))}
        {children}
      </div>
    </div>
  );
}

function rank(g: string): number {
  const i = (SHEET_GROUPS as readonly string[]).indexOf(g);
  return i < 0 ? SHEET_GROUPS.length : i;
}
